import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Shield, Ban, RotateCcw, Bot, Zap, Trash2, StickyNote, CreditCard } from "lucide-react";
import { api } from "../../api";
import { useAuth } from "../../AuthContext";
import { APageHead, ATable, ABadge, AMetric, AConfirm } from "../adminKit";

export default function AdminUserDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user: me } = useAuth();
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const [modal, setModal] = useState(null); // {action,title,message,requireReason,danger,fn}

  const load = () => api.get(`/admin/users/${id}`).then(({ data }) => setD(data)).catch(() => setD(false));
  useEffect(() => { load(); }, [id]);

  const act = (cfg) => setModal(cfg);
  const run = async (reason) => {
    try { await modal.fn(reason); toast.success("Action completed"); setModal(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/admin/users/${id}/note`, { note }); setNote(""); toast.success("Note added"); load(); }
    catch { toast.error("Failed"); }
  };

  if (d === null) return <div className="text-slate-500">Loading…</div>;
  if (d === false) return <div className="text-slate-400">User not found.</div>;
  const u = d.user;
  const isSuper = me?.role === "super_admin";
  const isAdmin = ["admin", "super_admin"].includes(me?.role);
  const isMod = ["moderator", "admin", "super_admin"].includes(me?.role);

  return (
    <div className="space-y-5">
      <Link to="/admin/users" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft size={16} /> Back to users</Link>
      <APageHead title={u.name || u.email} subtitle={u.email} />

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="cc-card rounded-2xl p-6 space-y-3">
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-full cc-gradient text-white font-600">{(u.name || u.email)[0].toUpperCase()}</span>
            <div>
              <div className="text-white cc-heading">{u.name}</div>
              <div className="flex gap-1.5 mt-1"><ABadge tone={u.status}>{u.status}</ABadge><ABadge tone="blue">{u.role.replace("_", " ")}</ABadge></div>
            </div>
          </div>
          <div className="pt-2 space-y-1.5 text-sm">
            <Row l="User ID" v={<span className="font-mono text-xs">{u.id}</span>} />
            <Row l="Plan" v={<ABadge tone={u.plan === "free" ? "default" : "green"}>{u.plan}</ABadge>} />
            <Row l="Verified" v={u.verified ? "Yes" : "No"} />
            <Row l="AI Access" v={u.ai_disabled ? <span className="text-red-300">Disabled</span> : "Enabled"} />
            <Row l="Signup" v={u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"} />
            <Row l="Last login" v={u.last_login ? new Date(u.last_login).toLocaleString() : "—"} />
          </div>
        </div>

        <div className="lg:col-span-2 space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <AMetric label="AI Q (mo)" value={d.usage.ai_questions} />
            <AMetric label="Reports" value={d.counts.reports} />
            <AMetric label="Disputes" value={d.counts.disputes} />
            <AMetric label="Vendors" value={d.counts.vendors} />
            <AMetric label="Documents" value={d.counts.documents} />
          </div>

          <div className="cc-card rounded-2xl p-5">
            <h3 className="cc-heading text-base font-500 text-white mb-4">Administrative Actions</h3>
            <div className="flex flex-wrap gap-2">
              {isMod && u.status !== "suspended" && <Btn testid="act-suspend" danger icon={Ban} onClick={() => act({ title: "Suspend account", message: "The user will be logged out and unable to sign in.", requireReason: true, danger: true, fn: (r) => api.post(`/admin/users/${id}/suspend`, { reason: r }) })}>Suspend</Btn>}
              {isMod && u.status === "suspended" && <Btn testid="act-restore" icon={RotateCcw} onClick={() => act({ title: "Restore account", message: "Re-activate this account?", requireReason: true, fn: (r) => api.post(`/admin/users/${id}/restore`, { reason: r }) })}>Restore</Btn>}
              {isSuper && <Btn testid="act-role" icon={Shield} onClick={() => act({ title: "Change role", message: "Enter reason. Role is set to admin (demo action).", requireReason: true, fn: (r) => api.patch(`/admin/users/${id}/role`, { role: "admin", reason: r }) })}>Make Admin</Btn>}
              {isAdmin && <Btn testid="act-ai" icon={Bot} onClick={() => act({ title: u.ai_disabled ? "Enable AI" : "Disable AI", message: "Toggle AI access for this user.", requireReason: true, fn: (r) => api.post(`/admin/users/${id}/ai?disable=${!u.ai_disabled}`, { reason: r }) })}>{u.ai_disabled ? "Enable AI" : "Disable AI"}</Btn>}
              {isAdmin && <Btn testid="act-reset" icon={Zap} onClick={() => act({ title: "Reset usage", message: "Reset this month's AI & report usage counters?", fn: () => api.post(`/admin/users/${id}/reset-usage`) })}>Reset Usage</Btn>}
              {isAdmin && <Btn testid="act-plan" icon={CreditCard} onClick={() => act({ title: "Grant Pro plan", message: "Apply a Pro plan override (demo billing).", requireReason: true, fn: (r) => api.post(`/admin/users/${id}/plan`, { plan: "pro", reason: r }) })}>Grant Pro</Btn>}
              {isSuper && <Btn testid="act-delete" danger icon={Trash2} onClick={() => act({ title: "Permanently delete", message: "This cannot be undone. Requires super-admin.", requireReason: true, danger: true, confirmLabel: "Delete permanently", fn: (r) => api.delete(`/admin/users/${id}?hard=true&reason=${encodeURIComponent(r)}`).then(() => nav("/admin/users")) })}>Delete</Btn>}
            </div>
          </div>

          <div className="cc-card rounded-2xl p-5">
            <h3 className="cc-heading text-base font-500 text-white mb-3 flex items-center gap-2"><StickyNote size={16} className="text-amber-300" /> Internal Notes</h3>
            <div className="flex gap-2 mb-3">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…" className="cc-input flex-1 rounded-lg px-3 py-2 text-sm" data-testid="note-input" />
              <button onClick={addNote} className="cc-btn-primary rounded-lg px-4 py-2 text-sm" data-testid="note-add">Add</button>
            </div>
            <div className="space-y-2">
              {d.notes.length === 0 && <p className="text-sm text-slate-500">No notes.</p>}
              {d.notes.map((n) => <div key={n.id} className="rounded-lg cc-surface border border-white/[0.05] px-3 py-2 text-sm"><span className="text-slate-200">{n.note}</span><div className="text-xs text-slate-500 mt-0.5">{n.author} · {new Date(n.created_at).toLocaleString()}</div></div>)}
            </div>
          </div>
        </div>
      </div>

      <div className="cc-card rounded-2xl p-5">
        <h3 className="cc-heading text-base font-500 text-white mb-3">Audit History</h3>
        <ATable columns={[
          { key: "action", label: "Action", render: (r) => <span className="font-mono text-xs">{r.action}</span> },
          { key: "resource", label: "Target", render: (r) => <span className="font-mono text-xs text-slate-500">{r.resource?.slice(0, 12) || "—"}</span> },
          { key: "created_at", label: "When", render: (r) => new Date(r.created_at).toLocaleString() },
        ]} rows={d.audit} loading={false} empty="No audit events." />
      </div>

      <AConfirm open={!!modal} {...(modal || {})} onCancel={() => setModal(null)} onConfirm={run} />
    </div>
  );
}

const Row = ({ l, v }) => <div className="flex items-center justify-between"><span className="text-slate-500">{l}</span><span className="text-slate-200">{v}</span></div>;
const Btn = ({ children, icon: Icon, danger, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm cc-nav-item ${danger ? "border-red-400/30 text-red-300" : "border-white/12 text-slate-200"}`}><Icon size={14} /> {children}</button>
);
